import { useCallback, useMemo, useRef, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import MainContainer from 'components/layout/MainContainer/MainContainer'
import { useTimezone } from '../../../lib/TimezoneProvider'
import { useOrbit } from 'providers/OrbitProvider'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { someVendorPermitLandingPages } from 'lib/auth-helpers'
import {
  getAllAccessibleClientIds,
  getAllAccessibleVendorIds,
} from 'components/PerformanceTable/PerformanceTable'
import { deepEqual, failOnHttpError } from '../../../lib/utils'
import { LeadsDataGrid } from './table/LeadTableDataGrid'
import {
  buildInitialColumnVisibilityModel,
  buildInitialFilterModel,
  buildInitialPaginationModel,
  buildInitialSortModel,
  buildCustomFieldTypes,
  buildLeadsRequestBody,
  dashedToUnderscored,
  toggleStatusInFilterModel,
} from './leadTableQuery'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { getIn } from 'components/Formik/forms'
import UploadDialog from 'components/UploadDialog'
import Box from '@mui/material/Box'
import {
  GridColumnVisibilityModel,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-premium'
import ExportStatusDialog, { ExportStatus } from './components/ExportStatusDialog'
import { LeadRow } from './table/leadTableTypes'
import { LeadCountRow } from './components/LeadCountRow'
import { useLeadTableMeta } from './useLeadTableMeta'
import {
  loadColumnOrder,
  loadColumnVisibilityModel,
  saveColumnOrder,
  saveColumnVisibilityModel,
} from './leadTablePreferences'
import { useQueryParam } from 'use-query-params'
import { DataGridTableSettingsAdapter, RawDataGridTableSettings } from 'components/DataGrid/dataGridTableSettingsAdapter'

function useDeepStableValue<T>(value: T): T {
  const ref = useRef(value)

  if (!deepEqual(ref.current, value)) {
    ref.current = value
  }

  return ref.current
}

export default function LeadsPage() {
  const {
    currentOrganization,
    accessibleClients,
    accessibleVendorContracts,
    accessibleVendors,
    accessibleClientContracts,
    getClientRole,
  } = useCurrentUser()
  const { remote, getRemoteId } = useOrbit()
  const { timezone } = useTimezone()

  const [refreshKey, setRefreshKey] = useState(0)
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [rowCount, setRowCount] = useState(0)
  const [exportStatus, setExportStatus] = useState<ExportStatus|null>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const permitAiAnalysis = !!getIn(currentOrganization, 'attributes.permitAiAnalysis')

  const allAccessibleClientIds = useMemo(() => {
    return getAllAccessibleClientIds(getRemoteId, accessibleClients, accessibleVendorContracts)
  }, [accessibleClients, accessibleVendorContracts, getRemoteId])

  const allAccessibleVendorIds = useMemo(() => {
    return getAllAccessibleVendorIds(getRemoteId, accessibleVendors, accessibleClientContracts)
  }, [accessibleVendors, accessibleClientContracts, getRemoteId])

  const userHasMultipleClients = allAccessibleClientIds.length > 1
  const userHasMultipleVendors = allAccessibleVendorIds.length > 1
  const permitLandingPages = someVendorPermitLandingPages({ accessibleVendors })
  const hasPermitEditRoleForSomeClient = useMemo(
    () => allAccessibleClientIds.some((clientId) => !!getIn(getClientRole(clientId), 'attributes.permitEdit')),
    [allAccessibleClientIds, getClientRole]
  )
  const permitUpload = hasPermitEditRoleForSomeClient
  const canDeleteLead = useCallback(
    (row: LeadRow) =>
      row.clientId != null &&
      !!getIn(getClientRole(String(row.clientId)), 'attributes.permitEdit'),
    [getClientRole]
  )

  const {
    loadingMetadata,
    clientIdToName,
    clientCampaignIdToName,
    vendorIdToName,
    degreeProgramIdToName,
    degreeProgramsByClient,
    campaignsByClient,
    statuses,
    dispositions,
    clientCampaignCustomAttributes,
    leadCustomFields,
  } = useLeadTableMeta({
    accessibleClientIds: allAccessibleClientIds,
  })

  const leadCustomFieldNames = useMemo(
    () => (leadCustomFields ?? []).map((field) => field.name),
    [leadCustomFields]
  )
  const customFieldTypes = useMemo(
    () => buildCustomFieldTypes(leadCustomFields ?? []),
    [leadCustomFields]
  )

  const [rawTableSettings, setTableSettings] = useQueryParam(
    'tableSettings',
    DataGridTableSettingsAdapter
  )

  const defaultPaginationModel = useMemo(() => buildInitialPaginationModel(), [])
  const defaultSortModel = useMemo(() => buildInitialSortModel(), [])
  const defaultFilterModel = useMemo(() => buildInitialFilterModel(timezone), [timezone])
  const defaultColumnOrder = useMemo(() => loadColumnOrder(), [])

  const paginationModel = rawTableSettings?.paginationModel ?? defaultPaginationModel

  const sortModel = useDeepStableValue(
    rawTableSettings?.sortModel ?? defaultSortModel
  )

  const filterModel = useDeepStableValue(
    rawTableSettings?.filterModel ?? defaultFilterModel
  )

  const columnOrder = useDeepStableValue(
    rawTableSettings?.columnOrder ?? defaultColumnOrder
  )

  const mergedColumnVisibilityModel = useMemo(() => ({
    ...buildInitialColumnVisibilityModel(
      clientCampaignCustomAttributes ?? [],
      leadCustomFieldNames
    ),
    ...loadColumnVisibilityModel(),
    ...rawTableSettings?.columnVisibilityModel,
  }), [
    clientCampaignCustomAttributes,
    leadCustomFieldNames,
    rawTableSettings?.columnVisibilityModel,
  ])

  const columnVisibilityModel = useDeepStableValue(
    mergedColumnVisibilityModel
  )

  const tableSettings = useMemo<RawDataGridTableSettings>(() => ({
    paginationModel,
    sortModel,
    filterModel,
    columnOrder,
    columnVisibilityModel,
    savedReportId: rawTableSettings?.savedReportId ?? null,
    savedReportName: rawTableSettings?.savedReportName ?? null,
  }), [
    paginationModel,
    sortModel,
    filterModel,
    columnOrder,
    columnVisibilityModel,
    rawTableSettings?.savedReportId,
    rawTableSettings?.savedReportName,
  ])

  const updateTableSettings = useCallback((
    patch: Partial<RawDataGridTableSettings> | ((current: RawDataGridTableSettings) => Partial<RawDataGridTableSettings>)
  ) => {
    setTableSettings((previous) => {
      const current: RawDataGridTableSettings = {
        paginationModel: previous?.paginationModel ?? paginationModel,
        sortModel: previous?.sortModel ?? sortModel,
        filterModel: previous?.filterModel ?? filterModel,
        columnOrder: previous?.columnOrder ?? columnOrder,
        columnVisibilityModel: previous?.columnVisibilityModel ?? columnVisibilityModel,
        savedReportId: previous?.savedReportId ?? null,
        savedReportName: previous?.savedReportName ?? null,
      }

      return {
        ...current,
        ...(typeof patch === 'function' ? patch(current) : patch),
      }
    })
  }, [
    setTableSettings,
    paginationModel,
    sortModel,
    filterModel,
    columnOrder,
    columnVisibilityModel,
  ])

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    updateTableSettings({ paginationModel: model })
  }, [updateTableSettings])

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    updateTableSettings({
      sortModel: model,
      paginationModel: {
        ...paginationModel,
        page: 0,
      },
    })
  }, [paginationModel, updateTableSettings])

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    updateTableSettings({
      filterModel: model,
      paginationModel: {
        ...paginationModel,
        page: 0,
      },
    })
  }, [paginationModel, updateTableSettings])

  const handleColumnOrderChange = useCallback((nextColumnOrder: string[]) => {
    saveColumnOrder(nextColumnOrder)
    updateTableSettings({ columnOrder: nextColumnOrder })
  }, [updateTableSettings])

  const handleColumnVisibilityModelChange = useCallback((model: GridColumnVisibilityModel) => {
    saveColumnVisibilityModel(model)
    updateTableSettings({ columnVisibilityModel: model })
  }, [updateTableSettings])

  const handleImport = useCallback(() => {
    setShowUploadDialog(true)
  }, [])

  const refreshTable = useCallback(() => {
    setRefreshKey((prev) => prev + 1)
  }, [])

  const handleToggleStatusFilter = useCallback((status: string) => {
    const normalizedStatus = dashedToUnderscored(status)

    updateTableSettings((current) => ({
      filterModel: toggleStatusInFilterModel(
        current.filterModel ?? buildInitialFilterModel(timezone),
        normalizedStatus
      ),
      paginationModel: {
        ...(current.paginationModel ?? buildInitialPaginationModel()),
        page: 0,
      },
    }))
  }, [timezone, updateTableSettings])

  const handleResetColumns = useCallback(() => {
    const nextVisibilityModel = buildInitialColumnVisibilityModel(
      clientCampaignCustomAttributes ?? [],
      leadCustomFieldNames
    )

    saveColumnOrder([])
    saveColumnVisibilityModel(nextVisibilityModel)

    updateTableSettings({
      columnOrder: [],
      columnVisibilityModel: nextVisibilityModel,
    })
  }, [clientCampaignCustomAttributes, leadCustomFieldNames, updateTableSettings])

  const handleResetFilters = useCallback(() => {
    updateTableSettings({
      paginationModel: {
        ...buildInitialPaginationModel(),
        pageSize: paginationModel.pageSize,
      },
      sortModel: buildInitialSortModel(),
      filterModel: buildInitialFilterModel(timezone),
    })
  }, [paginationModel.pageSize, timezone, updateTableSettings])

  const handleExport = useCallback(async () => {
    if(exportStatus) {
      setShowExportModal(true)
      return
    }

    const requestBody = buildLeadsRequestBody({
      paginationModel,
      sortModel,
      filterModel,
      timezone,
      customFieldTypes,
    })

    setExportStatus({ status: 'running' })
    setShowExportModal(true)

    const urlBuilder = remote.requestProcessor.urlBuilder
    const baseURL = urlBuilder.resourceURL('lead')
    fetch(`${baseURL}.csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
      .then(failOnHttpError)
      .then(response => response.json())
      .then(result => {
        setExportStatus({ status: 'queued', statusURL: result.status_url })
      })
      .catch(_error => {
        setExportStatus({ status: 'discarded', error: 'Failed to schedule export job' })
      })
  }, [exportStatus, paginationModel, sortModel, filterModel, timezone, customFieldTypes, remote.requestProcessor.urlBuilder])

  const gridProps = useMemo(() => {
    return {
      timezone,
      refreshKey,
      refreshTable,
      rowCount,
      paginationModel,
      onPaginationModelChange: handlePaginationModelChange,
      sortModel,
      onSortModelChange: handleSortModelChange,
      filterModel,
      columnVisibilityModel,
      onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
      columnOrder,
      onColumnOrderChange: handleColumnOrderChange,
      onFilterModelChange: handleFilterModelChange,
      clientIdToName,
      clientCampaignIdToName,
      vendorIdToName,
      degreeProgramIdToName,
      degreeProgramsByClient,
      campaignsByClient,
      statuses,
      dispositions,
      clientCampaignCustomAttributes,
      leadCustomFields,
      permitLandingPages,
      canDeleteLead,
      permitAiAnalysis,
      userHasMultipleClients,
      userHasMultipleVendors,
      onResetColumns: handleResetColumns,
      onResetFilters: handleResetFilters,
      onExport: handleExport,
      onImport: handleImport,
      exportStatus: exportStatus,
      permitUpload: permitUpload,
      tableSettings,
      updateTableSettings,
    }
  }, [timezone, refreshKey, refreshTable, rowCount, paginationModel, handlePaginationModelChange, sortModel, handleSortModelChange, filterModel, columnVisibilityModel, handleColumnVisibilityModelChange, columnOrder, handleColumnOrderChange, handleFilterModelChange, clientIdToName, clientCampaignIdToName, vendorIdToName, degreeProgramIdToName, degreeProgramsByClient, campaignsByClient, statuses, dispositions, clientCampaignCustomAttributes, leadCustomFields, permitLandingPages, canDeleteLead, permitAiAnalysis, userHasMultipleClients, userHasMultipleVendors, handleResetColumns, handleResetFilters, handleExport, handleImport, exportStatus, permitUpload, tableSettings, updateTableSettings])

  if (loadingMetadata) {
    return (
      <MainContainer fullWidth>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </div>
      </MainContainer>
    )
  }

  return (
    <MainContainer
      fullWidth
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Paper
        sx={{
          my: '24px',
          p: '18px 24px',
          boxShadow: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 2, flexShrink: 0 }}
        >
          <Typography variant='h1'>Leads</Typography>
        </Stack>

        <LeadCountRow
          filterModel={filterModel}
          timezone={timezone}
          refreshKey={refreshKey}
          onTotalChange={setRowCount}
          onToggleStatusFilter={handleToggleStatusFilter}
          customFieldTypes={customFieldTypes}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <LeadsDataGrid {...gridProps} />
        </Box>
      </Paper>

      {!!showExportModal && (
        <ExportStatusDialog
          exportStatus={exportStatus}
          setExportStatus={setExportStatus}
          setShowExportModal={setShowExportModal}
        />
      )}
      {showUploadDialog && (
        <UploadDialog
          onComplete={refreshTable}
          onClose={() => setShowUploadDialog(false)}
        />
      )}
    </MainContainer>
  )
}
